import { useRef, useEffect, useState, useMemo } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import "./Work.css";
import WorkItemcontainer from "./WorkitemContainer";
import workData from "./workData";
import Footer from "./Footer";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const Work = ({ lang, setWorkInlineContainerVisible }) => {
    const [selectedUrl, setSelectedUrl] = useState("");
    const [isVisible, setVisible] = useState(false);
    const [isIframeLoaded, setIframeLoaded] = useState(false);
    const [showIframeLoadingMessage, setShowIframeLoadingMessage] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [isCategorySticky, setCategorySticky] = useState(false);
    const previousCategoryRef = useRef("all");

    const containerRef = useRef(null);
    const blurRef = useRef(null);
    const whiteDimRef = useRef(null);
    const scrollContentRef = useRef(null);
    const hasSnapped = useRef(false);

    const categoryContainer = useRef(null);

    const filteredWorkData = useMemo(() => {
        if (!Array.isArray(workData)) return [];

        const allItems = workData.reduce((acc, work) => {
            if (Array.isArray(work.items)) {
                return [...acc, ...work.items];
            }
            return acc;
        }, []);

        if (selectedCategory === "all") {
            return allItems;
        }

        const filtered = allItems.filter((item) =>
            item.categorys && item.categorys.includes(selectedCategory)
        );

        return filtered;
    }, [selectedCategory]);

    const handleUrlClick = (url) => {
        setSelectedUrl("");
        setShowIframeLoadingMessage(true);
        setSelectedUrl(url);
        setVisible(true);
        setWorkInlineContainerVisible(true);
        setIframeLoaded(true);
    };

    const handleCategoryClick = (category) => {
        if (selectedCategory !== category) {
            previousCategoryRef.current = category;
            setSelectedCategory(category);
        }
        setTimeout(() => {
            // adjustFeaturedHeight();
            blurWhiteDimTween(true);
        }, 10);
    };

    const handleCloseIframe = () => {
        setVisible(false);
        setWorkInlineContainerVisible(false);
        setShowIframeLoadingMessage(false);
        setSelectedUrl("");
    };

    const checkCategorySticky = () => {
        if (!categoryContainer.current) return;
        // console.log(categoryContainer.current.getBoundingClientRect().top)
        if (categoryContainer.current.getBoundingClientRect().top < 26 && window.innerWidth < 1200)
            setCategorySticky(true);
        else
            setCategorySticky(false);
        // console.log("isCategorySticky: ", isCategorySticky);
    }

    const adjustFeaturedHeight = () => {
        // if (!containerRef.current) return;

        // const container = containerRef.current;
        // const allItemBoxes = container.querySelectorAll(".work-item-box");
        // const featuredBoxes = container.querySelectorAll(".featured");
        // // 높이를 한 번에 초기화
        // allItemBoxes.forEach((itemBox) => (itemBox.style.height = "auto"));

        // // 한 번만 offsetWidth 가져오기 (Layout Thrashing 방지)
        // requestAnimationFrame(() => {
        //     featuredBoxes.forEach((featuredBox) => {
        //         const featuredWidth = featuredBox.offsetWidth;
        //         if (featuredBox.style.height !== `${featuredWidth}px`) {
        //             featuredBox.style.height = `${featuredWidth}px`;
        //         }
        //     });
        // });
    };

    const tempScrollPos = useRef(0);

    const blurWhiteDimTween = (forceUpdate = false) => {
        if (!containerRef.current) return;
        const scrollPosition = containerRef.current.scrollTop;
        const containerHeight = containerRef.current.scrollHeight;
        // console.log("scrollPosition: ",scrollPosition)
        const ratio = scrollPosition / (containerHeight * 0.3);
        const blurValue = Math.min(ratio * 60, 60);
        const opacityValue = Math.min(ratio * 0.3, 0.3);

        if (forceUpdate) {
            blurRef.current.style.webkitBackdropFilter = `blur(${blurValue}px)`;
            blurRef.current.style.backdropFilter = `blur(${blurValue}px)`;
            whiteDimRef.current.style.opacity = `${opacityValue}`;
        } else {
            requestAnimationFrame(() => {
                if (!blurRef.current) return;
                blurRef.current.style.webkitBackdropFilter = `blur(${blurValue}px)`;
                blurRef.current.style.backdropFilter = `blur(${blurValue}px)`;
                whiteDimRef.current.style.opacity = `${opacityValue}`;
            });
        }
    };
    const handleResize = () => {
        if (containerRef.current) {
            tempScrollPos.current = containerRef.current.scrollTop; // 현재 스크롤 위치 저장
            // console.log(tempScrollPos);
        }

        ScrollTrigger.refresh();

        requestAnimationFrame(() => {
            // adjustFeaturedHeight();
            blurWhiteDimTween(true);
        });
        if (containerRef.current) {
            containerRef.current.scrollTop = tempScrollPos.current; // 기존 스크롤 위치 복원
        }
    };

    // snapTo 로직을 별도 함수로 분리해서 언제든 호출 가능하게 만듭니다.
    const manualSnapScroll = (direction = 'down') => {
        // console.log("manualSnapScroll: ", direction)
        if (!containerRef.current) return;

        containerRef.current.style.pointerEvents = 'none'; // 스크롤 도중 사용자 조작 막기

        // 뷰포트 높이 & 스냅 목표 지점 계산
        const viewportHeight = window.innerHeight;
        const targetDownSnapPos = viewportHeight - (window.innerWidth < 1200 ? -20 : 80);
        const targetUpSnapPos = 0;

        // 방향별로 스크롤할 목표 위치(targetScrollPos) 결정
        const targetScrollPos = direction === 'down' ? targetDownSnapPos : targetUpSnapPos;

        // GSAP으로 애니메이트하여 해당 위치로 스크롤
        gsap.to(containerRef.current, {
            scrollTo: targetScrollPos,
            duration: 0.4,
            ease: 'cubic.out',
            onComplete: () => {
                containerRef.current.style.pointerEvents = 'auto';
                hasSnapped.current = false;
            },
        });
    };

    useEffect(() => {
        if (!containerRef.current || !scrollContentRef.current) return;

        const container = containerRef.current;
        const scrollContent = scrollContentRef.current;
        const blurEl = blurRef.current;
        const whiteDimEl = whiteDimRef.current;
        hasSnapped.current = false;

        // setMobileView(window.innerWidth < 1200 ? true : false);
        // console.log("isMobileView :: ", isMobileView)

        // 초기 스타일 설정
        if (blurEl) {
            blurEl.style.webkitBackdropFilter = "blur(0px)";
            blurEl.style.backdropFilter = "blur(0px)";
        }
        if (whiteDimEl) {
            whiteDimEl.style.opacity = "0";
        }

        const viewportHeight = window.innerHeight;
        let activeTween = null;

        // ScrollTrigger 인스턴스 생성 후 변수에 저장
        const scrollTriggerInstance = ScrollTrigger.create({
            trigger: scrollContent,
            scroller: container,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.6,
            snap: {
                snapTo: (progress, self) => {
                    // console.log("progress:: ", progress)
                    const scrollPos = self.scroll();
                    const progressViewport = scrollPos / viewportHeight;
                    // console.log("self.direction: " + self.direction + ", progressViewport: " + progressViewport);
                    // 스크롤 방향 & 진행도에 따라 스냅 로직 수행
                    if (self.direction > 0 && progressViewport > 0.2 && progressViewport < 1.0) {
                        if (!hasSnapped.current) {
                            hasSnapped.current = true;
                            // 아래쪽으로 스냅
                            manualSnapScroll('down');
                            return false;  // snapTo가 false 면 snap 종료
                        }
                    } else if (self.direction < 0 && progressViewport < 0.8) {
                        if (!hasSnapped.current) {
                            hasSnapped.current = true;
                            // 위쪽으로 스냅
                            manualSnapScroll('up');
                            return false;
                        }
                    }
                    return false;
                },
                duration: 0.1,
                delay: 0,
                ease: "power2.out",
            },
            onUpdate: (self) => {
                blurWhiteDimTween();
                checkCategorySticky();
            },
            onSnapComplete: () => {
                hasSnapped.current = false;
            },
        });

        // 초기 호출
        // requestAnimationFrame(() => {
        //     blurWhiteDimTween();
        // });

        let startY = 0;
        let moveY = 0;
        const handleOverScroll = (event) => {
            event.stopPropagation();
            checkCategorySticky();
            if (!containerRef.current) return;
            const scrollTop = containerRef.current.scrollTop;
            if (scrollTop > 0) return;

            if (event.type === "touchstart") {
                startY = event.touches[0].clientY;
            } else if (event.type === "touchmove") {
                moveY = event.touches[0].clientY;
                if (moveY > startY) {
                    window.dispatchEvent(new CustomEvent("closeSubToMain"));
                }
            } else if (event.type === "wheel") {
                if (event.deltaY < 0) {
                    window.dispatchEvent(new CustomEvent("closeSubToMain"));
                }
            }
        };
        const handleTouch = () => {
        };

        // window.addEventListener("touchstart", handleOverScroll);
        // window.addEventListener("touchmove", handleOverScroll);
        // window.addEventListener("wheel", handleOverScroll);


        ScrollTrigger.refresh();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            // window.removeEventListener("touchstart", handleOverScroll);
            // window.removeEventListener("touchmove", handleOverScroll);
            // window.removeEventListener("wheel", handleOverScroll);

        };
    }, [selectedCategory, filteredWorkData]);

    const getTopValue = (isVisible, isIframeLoaded) => {
        const screenWidth = window.innerWidth;

        if (screenWidth > 800 && screenWidth < 1200) {
            return "40px";
        } else if (screenWidth >= 1200) {
            return "80px";
        } else {
            return isVisible && isIframeLoaded ? "60px" : `${window.innerHeight}px`;
        }
    };

    const workInlineContainerStyle = {
        pointerEvents: isVisible && isIframeLoaded ? "all" : "none",
        transition: "all 0.2s ease",
        opacity: isVisible && isIframeLoaded ? 1 : 0,
        transform: isVisible && isIframeLoaded ? "scale(1)" : "scale(0.95)",
        top: getTopValue(isVisible, isIframeLoaded),
        zIndex: 1000,
        background: "white",
    };

    const workInlineContainerBgStyle = {
        display: isVisible && showIframeLoadingMessage ? "block" : "none",
        cursor: "zoom-out",
        transition: "all 0.3s ease",
    };

    const workCategoryContainerStyle = {
        position: isCategorySticky ? 'fixed' : window.innerHeight > 1200 ? 'absolute' : 'relative',
        top: isCategorySticky ? window.innerHeight < 1200 ? '70px' : '40px' : '',
        zIndex: '10000'
        // bottom: isCategorySticky  ? '' 
    }
    const categoryStickyBgStyle = {
        opacity: isCategorySticky ? 1 : 0
    }

    return (
        <>
            <div ref={blurRef} className="work-background-blur"></div>
            <div ref={whiteDimRef} className="work-background-white"></div>
            <div ref={containerRef} className="work-scroll-container">
                <div ref={scrollContentRef} className="work-container-scrollcontent">
                    <div id="work" className="work-container">
                        <div className="work-top-textarea">
                            <div className="work-description">
                                {lang === 'ENG'
                                    ? <>We make creative experience with Technology<br /></>
                                    : <>우리는 기술을 통해 <br />창의적인 경험을 만듭니다</>
                                }
                            </div>
                            <div className="work-description-sub">
                                {lang === 'ENG'
                                    ? <>Our goal is to create meaningful connections between people,<br />
                                        products, and digital services through thoughtful design and user-centered approaches.</>
                                    : <>우리는 사려 깊은 디자인과 사용자 중심 접근을 통해<br />
                                        사람, 제품, 디지털 서비스 간의 의미 있는 연결을 만들어갑니다.</>}
                                <button
                                    className="work-gotohnine-button"
                                    onClick={() => window.open("https://hnine.com", "_blank")}
                                >
                                    Go to HNINE <img src={process.env.PUBLIC_URL + "/data/img/ic/arrow.png"} alt="arrow" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div ref={categoryContainer} className="work-background-bottom">
                        <div className="work-category-nav-mobile-gradient" style={categoryStickyBgStyle}></div>
                        <div className="work_title_category_container">
                            <div className="work_title">Project</div>
                            <div className="work_category_container" style={workCategoryContainerStyle}>
                                <div className="work-category-nav">
                                    <button
                                        className={`work-category-button ${selectedCategory === "all" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("all");
                                            manualSnapScroll();
                                        }}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "xr" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("xr");
                                            manualSnapScroll();
                                        }}
                                    >
                                        XR
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "rnd" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("rnd");
                                            manualSnapScroll();
                                        }}
                                    >
                                        R&D
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "hifi" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("hifi");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Hi-Fi Prototype
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "app" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("app");
                                            manualSnapScroll();
                                        }}
                                    >
                                        App
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "3d" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("3d");
                                            manualSnapScroll();
                                        }}
                                    >
                                        3D
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "motion" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("motion");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Motion
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "visual" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("visual");
                                            manualSnapScroll();
                                        }}
                                    >
                                        VI
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "movie" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("movie");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Introduction Movie
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "media" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("media");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Media Art
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "exhibition" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("exhibition");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Exhibition
                                    </button>
                                    <button
                                        className={`work-category-button ${selectedCategory === "award" ? "active" : ""}`}
                                        onClick={() => {
                                            handleCategoryClick("award");
                                            manualSnapScroll();
                                        }}
                                    >
                                        Award
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="work-item-area">
                            {filteredWorkData.length > 0 ? (
                                <WorkItemcontainer
                                    lang={lang}
                                    containerTitle="Filtered Items"
                                    items={filteredWorkData}
                                    onUrlClick={handleUrlClick}
                                    selectedCategory={selectedCategory}
                                />
                            ) : (
                                <p>No items available.</p>
                            )}
                        </div>
                        <Footer
                            lang={lang}
                            isContactPage={false} />
                    </div>
                </div>
            </div>
            <div
                className="work-inline-container-background"
                style={workInlineContainerBgStyle}
                onClick={handleCloseIframe}
            ></div>
            <div className="work-inline-container" style={workInlineContainerStyle}>
                {selectedUrl !== "" ? (
                    <iframe src={selectedUrl} title="inline-content"></iframe>
                ) : (
                    ""
                )}
                <div className="work-inline-container-hidden-button" onClick={handleCloseIframe}></div>
                <div className="work-hidden-home"></div>
            </div>
        </>
    );
};

export default Work;